import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { useNumericInput } from '@jobcost-monorepo/utils';
import React, { InputHTMLAttributes, KeyboardEvent } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '../../index';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  errorName: string;
  label: string;
  isCurrency?: boolean;
  isPercent?: boolean;
  isDecimal?: boolean;
  precision?: number;
  allowNegativeNumber?: boolean;
  min?: number;
  max?: number;
  defaultValue?: number;
  width?: string;
}

const CurrencyInputForm = ({
  name,
  type = 'text',
  errorName,
  label,
  min,
  max,
  defaultValue,
  isPercent,
  isDecimal,
  precision,
  allowNegativeNumber = false,
  onBlur,
  onChange,
  width = 'w-full',
  ...rest
}: Props) => {
  const { control, trigger, clearErrors } = useFormContext();
  const {
    field: { value, onChange: onChangeRender, ref },
    formState: { errors },
  } = useController({ control, name });
  const { t } = useTranslation();
  const error = errors?.[errorName]?.message ?? '';
  const errorTranslated = t(error as string);
  const currencySymbol = '€';

  const { format, setIsFocus, handleKeyPress, parse, setCheckedFalsyValue } =
    useNumericInput({
      name,
      isCurrency: true,
      isPercent,
      isDecimal,
      precision,
      allowNegativeNumber,
      customSymbol: currencySymbol,
    });

  return (
    <div className="w-full">
      <label
        htmlFor={name}
        className="block text-sm font-medium text-gray-700 dark:text-white"
      >
        {label}
      </label>
      <div className="relative mt-1 rounded-md shadow-sm">
        <Input
          {...ref}
          type={type}
          name={name}
          id={name}
          min={min}
          max={max}
          defaultValue={
            defaultValue && defaultValue >= 0 ? defaultValue : undefined
          }
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            !!errorName && clearErrors(name);
            onChange && onChange(e);
            onChangeRender(parse(e.target.value));
          }}
          onBlur={(e: any) => {
            onBlur && onBlur(e);
            setIsFocus(false);
            setCheckedFalsyValue(false);
            trigger(name);
          }}
          onKeyDown={(ev: KeyboardEvent<HTMLInputElement>) => {
            handleKeyPress(ev);
          }}
          onFocus={() => {
            setIsFocus(true);
          }}
          value={format(value) || ''}
          className={
            errorTranslated &&
            'block w-full rounded-md border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm'
          }
          autoComplete="off"
          width={width}
          {...rest}
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          {errorTranslated ? (
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          ) : (
            <span className="h-5 w-5 " />
          )}
        </div>
      </div>
      <p className="h-3 text-xs my-1 ml-1 text-red-600" id="error">
        {errorTranslated}
      </p>
    </div>
  );
};
export default CurrencyInputForm;
